'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { AlertCircle, UserCheck } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import CustomCalendar from '@/components/dev/customCalendar'
import PatientDetailsForm from '@/components/booking/PatientDetailsForm'
import StepActions from '@/components/booking/step-actions'
import type { Doc } from '@/convex/_generated/dataModel'
import type { BookingData, BookingSetter } from '@/types/booking'

type PatientDoc = Doc<'patients'>

interface StepNewPatientDetailsProps {
  bookingData: BookingData
  setBookingData: BookingSetter
  existingPatientsForPhone?: PatientDoc[]
  onUseExistingPatient?: (patient: PatientDoc) => void
  onNext: () => void
  onPrevious: () => void
}

function joinName(first: string, middle: string, last: string) {
  return [first, middle, last].filter(Boolean).join(' ').trim()
}

function normalizePersonName(value: string | null | undefined) {
  return String(value ?? '')
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const StepNewPatientDetails: React.FC<StepNewPatientDetailsProps> = ({
  bookingData,
  setBookingData,
  existingPatientsForPhone = [],
  onUseExistingPatient,
  onNext,
  onPrevious,
}) => {
  const [firstName, setFirstName] = useState('')
  const [middleName, setMiddleName] = useState('')
  const [lastName, setLastName] = useState('')

  const [dirtyFirst, setDirtyFirst] = useState(false)
  const [dirtyLast, setDirtyLast] = useState(false)
  const [dirtyEmail, setDirtyEmail] = useState(false)
  const [dobOpen, setDobOpen] = useState(false)

  const fullName = useMemo(
    () => joinName(firstName, middleName, lastName),
    [firstName, middleName, lastName],
  )

  const duplicatePatient = useMemo(() => {
    const normalizedFullName = normalizePersonName(fullName)
    if (!normalizedFullName) return null

    return (
      existingPatientsForPhone.find(
        (patient) =>
          normalizePersonName((patient as any).name) === normalizedFullName,
      ) ?? null
    )
  }, [existingPatientsForPhone, fullName])

  useEffect(() => {
    setBookingData((prev) => ({
      ...prev,
      name: fullName,
      patientMode: 'new',
      existingPatientId: null,
      existingPatientHumanId: null,
      preferredPsychologist: null,
    }))
  }, [fullName, setBookingData])

  const today = useMemo(() => {
    const date = new Date()
    date.setHours(0, 0, 0, 0)
    return date
  }, [])

  const minDob = useMemo(
    () => new Date(today.getFullYear() - 120, 0, 1),
    [today],
  )

  const dob = bookingData.dob
  const validFirst = firstName.trim().length > 0
  const validLast = lastName.trim().length > 0
  const validDob = !!dob && dob <= today && dob >= minDob
  const emailSupplied = (bookingData.email || '').trim().length > 0
  const validEmail =
    !emailSupplied || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingData.email)

  const hasDuplicateNameForPhone = !!duplicatePatient

  const isValid =
    validFirst &&
    validLast &&
    validDob &&
    !!bookingData.gender &&
    validEmail &&
    !hasDuplicateNameForPhone

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
          Tell us who the appointment is for
        </h2>
        <p className="mt-2 text-sm text-gray-600 sm:text-base">
          We couldn’t find the right profile automatically. You can add a new
          patient, but the same name cannot be reused for this verified phone
          number.
        </p>
      </div>

      <Card className="border shadow-lg">
        <CardHeader className="pb-0" />
        <CardContent className="flex flex-col gap-5 px-4 pb-5 sm:gap-6 sm:px-6 sm:pb-6">
          <PatientDetailsForm
            visible
            firstName={firstName}
            middleName={middleName}
            lastName={lastName}
            setFirstName={setFirstName}
            setMiddleName={setMiddleName}
            setLastName={setLastName}
            dirtyFirst={dirtyFirst}
            setDirtyFirst={setDirtyFirst}
            dirtyLast={dirtyLast}
            setDirtyLast={setDirtyLast}
            dirtyEmail={dirtyEmail}
            setDirtyEmail={setDirtyEmail}
            validFirst={validFirst}
            validLast={validLast}
            validEmail={validEmail}
            dob={bookingData.dob}
            setDob={(date) =>
              setBookingData((prev) => ({
                ...prev,
                dob: date ?? undefined,
              }))
            }
            minDob={minDob}
            maxDob={today}
            dobOpen={dobOpen}
            setDobOpen={setDobOpen}
            gender={bookingData.gender}
            setGender={(gender) =>
              setBookingData((prev) => ({
                ...prev,
                gender,
              }))
            }
            email={bookingData.email}
            setEmail={(email) =>
              setBookingData((prev) => ({
                ...prev,
                email,
              }))
            }
            calculateAge={(date) => {
              const now = new Date()
              let years = now.getFullYear() - date.getFullYear()
              let months = now.getMonth() - date.getMonth()
              let days = now.getDate() - date.getDate()

              if (days < 0) {
                months -= 1
                days = Math.abs(days)
              }

              if (months < 0) {
                years -= 1
                months += 12
              }

              return `${years}Y ${months}M ${days}D`
            }}
            CustomCalendar={CustomCalendar}
          />

          {duplicatePatient && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />

                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-semibold text-amber-900">
                    This patient already exists for this phone number
                  </h4>

                  <p className="mt-1 text-xs leading-5 text-amber-800 sm:text-sm">
                    A ReACH profile named{' '}
                    <span className="font-semibold">
                      {(duplicatePatient as any).name}
                    </span>{' '}
                    is already linked to this verified phone number.
                  </p>

                  {(duplicatePatient as any).patient_id && (
                    <p className="mt-1 text-xs text-amber-700">
                      ReACH ID:{' '}
                      <span className="font-medium">
                        {(duplicatePatient as any).patient_id}
                      </span>
                    </p>
                  )}

                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    {onUseExistingPatient && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => onUseExistingPatient(duplicatePatient)}
                        className="rounded-full bg-amber-700 text-white hover:bg-amber-800"
                      >
                        <UserCheck className="mr-2 h-4 w-4" />
                        Use existing profile
                      </Button>
                    )}

                    <p className="text-xs leading-5 text-amber-700 sm:self-center">
                      Or change the name if this is a different patient.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <StepActions
            onPrevious={onPrevious}
            onNext={onNext}
            nextDisabled={!isValid}
            previousLabel="Back"
            nextLabel="Continue"
          />
        </CardContent>
      </Card>
    </div>
  )
}

export default StepNewPatientDetails
